(() => {
  if (window.MTEEditorial) {
    window.MTEEditorial.scan(document);
    return;
  }

  const instances = new WeakMap();
  const activeRoots = new Set();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => reducedMotion.matches ? 'auto' : 'smooth';
  const clamp = (value, minimum, maximum) => Math.max(minimum, Math.min(value, maximum));

  function initialize(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const disposers = [];
    const on = (element, event, handler, options = {}) => {
      element?.addEventListener(event, handler, { ...options, signal: controller.signal });
    };
    const state = { controller, disposers, selectBlock: null };
    instances.set(root, state);
    activeRoots.add(root);

    const track = root.querySelector('[data-editorial-track]');
    if (track) {
      const cards = Array.from(track.children).filter(element => element.hasAttribute('data-mte-block'));
      const dots = Array.from(root.querySelectorAll('[data-editorial-dot]'));
      const fill = root.querySelector('[data-editorial-fill]');
      let frame = 0;
      const step = () => {
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return cards.length ? cards[0].getBoundingClientRect().width + gap : track.clientWidth;
      };
      const update = () => {
        frame = 0;
        const current = clamp(Math.round(track.scrollLeft / Math.max(1, step())), 0, cards.length - 1);
        dots.forEach((dot, index) => dot.classList.toggle('is-active', current === index));
        if (fill) {
          const total = track.scrollWidth;
          const remaining = Math.max(0, total - track.clientWidth);
          const ratio = remaining ? clamp(track.scrollLeft / remaining, 0, 1) : 0;
          const fraction = total ? Math.min(1, track.clientWidth / total) : 1;
          const width = Math.max(16, fraction * 100);
          fill.style.width = `${width}%`;
          fill.style.transform = `translateX(${width ? (100 - width) * ratio / (width / 100) : 0}%)`;
        }
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };
      const scrollBy = direction => track.scrollBy({ left: direction * step(), behavior: behavior() });
      on(root.querySelector('[data-editorial-prev]'), 'click', () => scrollBy(-1));
      on(root.querySelector('[data-editorial-next]'), 'click', () => scrollBy(1));
      on(track, 'scroll', schedule, { passive: true });
      on(track, 'keydown', event => {
        if (event.target !== track) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          scrollBy(event.key === 'ArrowRight' ? 1 : -1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          track.scrollTo({ left: event.key === 'Home' ? 0 : track.scrollWidth, behavior: behavior() });
        }
      });
      if ('ResizeObserver' in window) {
        const resize = new ResizeObserver(schedule);
        resize.observe(track);
        disposers.push(() => resize.disconnect());
      } else {
        on(window, 'resize', schedule, { passive: true });
      }
      state.selectBlock = blockId => {
        const card = cards.find(element => element.dataset.mteBlock === blockId);
        if (!card) return;
        const left = card.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft;
        track.scrollTo({ left, behavior: 'auto' });
        update();
      };
      disposers.push(() => cancelAnimationFrame(frame));
      update();
    }

    const tabs = Array.from(root.querySelectorAll('[data-editorial-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-editorial-panel]'));
    if (tabs.length && panels.length) {
      const count = Math.min(tabs.length, panels.length);
      const stage = root.querySelector('.ig-stage');
      const rail = root.querySelector('.ig-rail');
      const pauses = new Set();
      const interval = Math.max(2000, Number(root.dataset.interval) || 4200);
      const enabled = root.dataset.autoplay === 'true' && !window.Shopify?.designMode;
      let current = clamp((Number(root.dataset.initialSlide) || 1) - 1, 0, count - 1);
      let timer = 0;
      let visible = !('IntersectionObserver' in window);
      let pointer = null;

      const show = (index, focus = false) => {
        current = (index + count) % count;
        panels.forEach((panel, panelIndex) => {
          const selected = panelIndex === current;
          panel.classList.toggle('active', selected);
          panel.setAttribute('aria-hidden', String(!selected));
          panel.inert = !selected;
        });
        tabs.forEach((tab, tabIndex) => {
          const selected = tabIndex === current;
          tab.classList.toggle('active', selected);
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
        });
        const activeTab = tabs[current];
        if (rail && activeTab && rail.scrollHeight > rail.clientHeight + 4) {
          const targetTop = Math.max(0, activeTab.offsetTop - (rail.clientHeight - activeTab.offsetHeight) / 2);
          rail.scrollTo({ top: targetTop, behavior: behavior() });
        }
        if (focus && activeTab) activeTab.focus({ preventScroll: true });
      };
      const stop = () => {
        if (timer) window.clearInterval(timer);
        timer = 0;
      };
      const resume = () => {
        stop();
        if (enabled && visible && !document.hidden && !pauses.size && !reducedMotion.matches && count > 1) {
          timer = window.setInterval(() => show(current + 1), interval);
        }
      };
      const pause = reason => { pauses.add(reason); stop(); };
      const unpause = reason => { pauses.delete(reason); resume(); };
      let manualUntil = 0;
      tabs.forEach((tab, index) => {
        on(tab, 'click', () => {
          manualUntil = Date.now() + 8000;
          show(index);
          resume();
          if (stage && window.innerWidth <= 880) {
            const rect = stage.getBoundingClientRect();
            if (rect.top < 72) {
              stage.scrollIntoView({ behavior: reducedMotion.matches ? 'instant' : 'smooth', block: 'start' });
            }
          }
        });
        on(tab, 'keydown', event => {
          let next = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = current + 1;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = current - 1;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = count - 1;
          if (next === null) return;
          event.preventDefault();
          manualUntil = Date.now() + 8000;
          show(next, true);
        });
      });
      if (!reducedMotion.matches && count > 1 && root.id === 'mte-ingredients') {
        let scrollFrame = 0;
        const onScrollStep = () => {
          scrollFrame = 0;
          if (Date.now() < manualUntil || pauses.size) return;
          const anchor = stage || root.querySelector('.ig-stage-grid') || root;
          const rect = anchor.getBoundingClientRect();
          const vh = window.innerHeight || 800;
          if (rect.bottom <= 0 || rect.top >= vh) return;
          const startY = vh * 0.58;
          const endY = vh * 0.06;
          const progress = clamp((startY - rect.top) / Math.max(1, startY - endY), 0, 0.9999);
          const targetIdx = Math.min(count - 1, Math.floor(progress * count));
          if (targetIdx !== current) {
            show(targetIdx);
            resume();
          }
        };
        on(window, 'scroll', () => {
          if (!scrollFrame) scrollFrame = requestAnimationFrame(onScrollStep);
        }, { passive: true });
        disposers.push(() => { if (scrollFrame) cancelAnimationFrame(scrollFrame); });
      }
      [stage, rail].forEach((element, index) => {
        on(element, 'mouseenter', () => pause(`hover-${index}`));
        on(element, 'mouseleave', () => unpause(`hover-${index}`));
        on(element, 'focusin', () => pause(`focus-${index}`));
        on(element, 'focusout', event => {
          if (!element.contains(event.relatedTarget)) unpause(`focus-${index}`);
        });
      });
      on(stage, 'pointerdown', event => {
        if (event.pointerType !== 'touch') return;
        pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
        pause('touch');
      });
      on(stage, 'pointerup', event => {
        if (!pointer || pointer.id !== event.pointerId) return;
        const deltaX = event.clientX - pointer.x;
        const deltaY = event.clientY - pointer.y;
        if (Math.abs(deltaX) > 60 && Math.abs(deltaX) > Math.abs(deltaY) * 1.4) {
          manualUntil = Date.now() + 8000;
          show(current + (deltaX < 0 ? 1 : -1));
        }
        pointer = null;
        unpause('touch');
      });
      on(stage, 'pointercancel', () => { pointer = null; unpause('touch'); });
      on(document, 'visibilitychange', resume);
      on(reducedMotion, 'change', resume);
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          visible = entries.some(entry => entry.isIntersecting);
          resume();
        }, { threshold: 0.25 });
        observer.observe(root);
        disposers.push(() => observer.disconnect());
      }
      state.selectBlock = blockId => {
        const index = tabs.findIndex(tab => tab.dataset.mteBlock === blockId);
        if (index >= 0) { pause('editor'); show(index); }
      };
      on(root, 'shopify:block:deselect', () => unpause('editor'));
      disposers.push(stop);
      show(current);
      resume();
    }
  }

  function destroy(root) {
    const state = instances.get(root);
    if (!state) return;
    state.controller.abort();
    state.disposers.forEach(dispose => dispose());
    activeRoots.delete(root);
    instances.delete(root);
  }

  function rootsWithin(container) {
    const roots = Array.from(container.querySelectorAll?.('[data-mte-editorial]') || []);
    if (container.matches?.('[data-mte-editorial]')) roots.unshift(container);
    return roots;
  }

  function scan(container) { rootsWithin(container).forEach(initialize); }

  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => rootsWithin(event.target).forEach(destroy));
  document.addEventListener('shopify:block:select', event => {
    const root = event.target.closest?.('[data-mte-editorial]');
    if (!root) return;
    initialize(root);
    const blockId = event.detail?.blockId || event.target.closest?.('[data-mte-block]')?.dataset.mteBlock;
    if (blockId) instances.get(root)?.selectBlock?.(blockId);
  });
  window.addEventListener('pagehide', () => activeRoots.forEach(destroy));
  window.addEventListener('pageshow', () => scan(document));
  window.MTEEditorial = { version: '1.0.0', scan, initialize, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
})();

(() => {
  const rootSelector = '#MainContent[data-dv-art-direction="r10"]';
  if (window.DearVArtDirection) {
    return;
  }
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  let state;
  const revealSelectors = [
    '.mte-callouts .benefit', '.mte-compound .step', '.mte-ingredient-card',
    '#mte-ingredients .ig-stage', '#mte-ingredients .ig-chip',
    '.mte-ugc-card', '.dv-contrast-box--old', '.dv-contrast-box--new',
    '.dv-clean-label', '.mte-compare-table', '.mte-reviews-summary', '.mte-review-card',
  ];
  function destroy() {
    if (!state) return;
    state.observers.forEach(observer => observer.disconnect());
    state.abort.abort();
    state.root.classList.remove('dv-motion-ready');
    state = undefined;
  }
  function mount() {
    const root = document.querySelector(rootSelector);
    if (!root) return;
    destroy();
    const abort = new AbortController();
    state = { root, abort, observers: [] };
    const elements = new Set();
    revealSelectors.forEach(selector => {
      root.querySelectorAll(selector).forEach((element, index) => {
        if ((document.body.classList.contains('mte-page--just-one') || document.body.classList.contains('mte-page--pineapple-crush')) && element.closest('.dv-ritual-timeline')) return;
        element.classList.add('dv-reveal', `dv-reveal--delay-${index % 4 + 1}`);
        elements.add(element);
      });
    });
    const immediate = media.matches || !('IntersectionObserver' in window);
    if (immediate) {
      elements.forEach(element => element.classList.add('is-inview'));
      root.classList.add('dv-motion-ready');
    } else {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview');
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.14, rootMargin: '0px 0px -40px 0px' });
      elements.forEach(element => observer.observe(element));
      state.observers.push(observer);
      root.classList.add('dv-motion-ready');
    }
    const button = root.querySelector('.mte-buy-button, mte-product-component [ref="addToCartButton"]');
    const sticky = root.querySelector('.dv-sticky-bar');
    if (button && sticky) {
      let stickyObserver;
      const update = entry => {
        const header = root.querySelector('.mte-header');
        const headerBottom = header?.getBoundingClientRect().height || 0;
        const rect = entry?.boundingClientRect || button.getBoundingClientRect();
        const visible = entry ? entry.isIntersecting : rect.bottom > headerBottom && rect.top < innerHeight;
        const show = !visible && rect.bottom <= headerBottom;
        sticky.hidden = false;
        sticky.dataset.dvStickyManaged = 'true';
        sticky.classList.toggle('is-available', show);
        sticky.inert = !show;
        sticky.setAttribute('aria-hidden', String(!show));
        sticky.style.transform = show ? 'translateY(0)' : 'translateY(110%)';
        sticky.style.transition = media.matches ? 'none' : 'transform 0.35s cubic-bezier(0.16, 1, 0.3, 1)';
      };
      const observeSticky = () => {
        stickyObserver?.disconnect();
        if ('IntersectionObserver' in window) {
          const offset = root.querySelector('.mte-header')?.getBoundingClientRect().height || 0;
          stickyObserver = new IntersectionObserver(([entry]) => update(entry), { threshold: 0, rootMargin: `-${offset}px 0px 0px 0px` });
          stickyObserver.observe(button);
          state.observers.push(stickyObserver);
        }
        update();
      };
      observeSticky();
      window.addEventListener('resize', observeSticky, { signal: abort.signal, passive: true });
      let stickyFrame = 0;
      window.addEventListener('scroll', () => {
        if (stickyFrame) return;
        stickyFrame = requestAnimationFrame(() => { stickyFrame = 0; update(); });
      }, { signal: abort.signal, passive: true });
      abort.signal.addEventListener('abort', () => cancelAnimationFrame(stickyFrame), { once: true });
    }
  }
  const safeMount = () => {
    try { mount(); }
    catch {
      document.querySelector(rootSelector)?.classList.remove('dv-motion-ready');
      document.querySelector(rootSelector)?.querySelectorAll('.dv-reveal').forEach(element => element.classList.add('is-inview'));
    }
  };
  document.addEventListener('shopify:section:load', safeMount);
  document.addEventListener('shopify:section:unload', safeMount);
  window.addEventListener('pagehide', destroy);
  window.addEventListener('pageshow', event => { if (event.persisted) safeMount(); });
  media.addEventListener('change', safeMount);
  window.DearVArtDirection = { version: 'r10', mount: safeMount, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', safeMount, { once: true });
  else safeMount();
})();

(() => {
  if (window.DearVRitualTimeline) {
    window.DearVRitualTimeline.scan(document);
    return;
  }

  const selector = 'body:is(.mte-page--just-one, .mte-page--pineapple-crush) #mte-compound .dv-ritual-timeline';
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileLayout = window.matchMedia('(max-width: 749px)');
  const instances = new Map();
  const clamp = value => Math.max(0, Math.min(1, value));
  let frame = 0;
  let geometryDirty = true;

  function schedule(measure = false) {
    geometryDirty ||= measure;
    if (!frame && instances.size) frame = requestAnimationFrame(update);
  }

  function update() {
    frame = 0;
    const scrollTop = window.scrollY;
    const viewport = window.innerHeight;
    const mobile = mobileLayout.matches;
    const reduced = motion.matches;

    if (geometryDirty) {
      for (const [timeline, state] of instances) {
        const top = timeline.getBoundingClientRect().top + scrollTop;
        const height = timeline.offsetHeight;
        const anchors = state.steps.map(step => {
          const node = step.querySelector('.dv-ritual-timeline__node');
          return step.offsetTop + (node ? node.offsetTop + node.offsetHeight / 2 : 0);
        });
        state.geometry = { top, height, anchors };
      }
      geometryDirty = false;
    }

    const updates = [];
    for (const [timeline, state] of instances) {
      const { top, height, anchors } = state.geometry;
      let progress;
      let active;
      if (reduced) {
        progress = 1;
        active = state.steps.map(() => true);
      } else if (mobile) {
        const readingPoint = scrollTop + viewport * .74 - top;
        progress = clamp((readingPoint - 16) / Math.max(1, height - 32));
        active = anchors.map(anchor => readingPoint >= anchor);
      } else {
        const journey = (scrollTop + viewport * .78 - top) / Math.max(1, height * .82);
        progress = clamp(journey);
        active = state.steps.map((_, index) => journey >= index / Math.max(1, state.steps.length - 1));
      }
      updates.push({ timeline, state, progress, active });
    }

    for (const { timeline, state, progress, active } of updates) {
      const fill = progress.toFixed(4);
      if (state.fill !== fill) {
        timeline.style.setProperty('--dv-timeline-fill', fill);
        state.fill = fill;
      }
      state.steps.forEach((step, index) => {
        if (state.active[index] !== active[index]) step.classList.toggle('is-step-active', active[index]);
      });
      state.active = active;
      timeline.classList.add('is-timeline-ready');
    }
  }

  function initialize(timeline) {
    if (instances.has(timeline)) return;
    const steps = Array.from(timeline.children).filter(element => element.classList.contains('mte-r7-step'));
    if (!steps.length) return;
    const state = { steps, active: [], fill: null, resize: null, geometry: null };
    instances.set(timeline, state);
    if ('ResizeObserver' in window) {
      state.resize = new ResizeObserver(() => schedule(true));
      state.resize.observe(timeline);
      steps.forEach(step => state.resize.observe(step));
      const main = timeline.closest('#MainContent');
      if (main) state.resize.observe(main);
    }
    schedule(true);
  }

  function scan(scope = document) {
    scope.querySelectorAll?.(selector).forEach(initialize);
    if (scope.matches?.(selector)) initialize(scope);
    schedule(true);
  }

  function destroy(scope = document) {
    for (const [timeline, state] of instances) {
      if (scope !== document && scope !== timeline && !scope.contains(timeline)) continue;
      state.resize?.disconnect();
      timeline.classList.remove('is-timeline-ready');
      timeline.style.removeProperty('--dv-timeline-fill');
      state.steps.forEach(step => step.classList.remove('is-step-active'));
      instances.delete(timeline);
    }
    if (!instances.size) {
      cancelAnimationFrame(frame);
      frame = 0;
    }
  }

  window.addEventListener('scroll', () => schedule(), { passive: true });
  window.addEventListener('resize', () => schedule(true), { passive: true });
  window.visualViewport?.addEventListener('resize', () => schedule(true), { passive: true });
  document.addEventListener('load', event => {
    if (event.target instanceof HTMLImageElement) schedule(true);
  }, true);
  motion.addEventListener('change', () => schedule(true));
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  window.addEventListener('pagehide', () => destroy());
  window.addEventListener('pageshow', () => scan(document));
  document.fonts?.ready.then(() => schedule(true));
  window.DearVRitualTimeline = { version: '17.4.3', scan, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
})();


(() => {
  if (window.DearVR17Buybox) {
    window.DearVR17Buybox.scan(document);
    return;
  }

  const instances = new Map();
  const dateFormatter = new Intl.DateTimeFormat('es-ES', { timeZone: 'UTC', day: 'numeric', month: 'long' });
  const madridCalendar = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' });

  // Work with UTC calendar dates after reading today's date in Madrid. This
  // avoids the visitor's local time zone and daylight-saving transitions.
  function deliveryDates(now = new Date()) {
    const parts = Object.fromEntries(madridCalendar.formatToParts(now).filter(part => part.type !== 'literal').map(part => [part.type, part.value]));
    const start = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
    const addBusinessDays = amount => {
      const day = new Date(start);
      let counted = 0;
      while (counted < amount) {
        day.setUTCDate(day.getUTCDate() + 1);
        if (day.getUTCDay() !== 0 && day.getUTCDay() !== 6) counted += 1;
      }
      return { iso: day.toISOString().slice(0, 10), label: dateFormatter.format(day) };
    };
    return { start: addBusinessDays(2), end: addBusinessDays(3) };
  }

  function refreshDelivery(scope = document) {
    const dates = deliveryDates();
    scope.querySelectorAll('[data-dv-delivery-promise]').forEach(promise => {
      [['[data-dv-delivery-start]', dates.start], ['[data-dv-delivery-end]', dates.end]].forEach(([selector, date]) => {
        const node = promise.querySelector(selector);
        if (!node) return;
        node.dateTime = date.iso;
        node.textContent = date.label;
      });
    });
  }

  function initialize(root) {
    if (instances.has(root)) return;
    const supply = root.querySelector('[data-mte-supply][data-dv-r17="true"]');
    if (!supply) return;
    const abort = new AbortController();
    const sticky = root.closest('main')?.querySelector('[data-dear-v-sticky][data-pack-summary]');
    const stickyButton = sticky?.querySelector('[data-dv-pack-add]');
    const gifts = Array.from(supply.querySelectorAll('[data-dv-gift]'));
    let priorTier;
    const update = detail => {
      const tier = String(detail?.tier || root.state?.tier || supply.querySelector('input:checked')?.value || '3');
      const selected = supply.querySelector(`[data-tier="${tier}"]`);
      if (!selected) return;
      let unlockedCount = 0;
      gifts.forEach(gift => {
        const unlocked = Number(tier) >= Number(gift.dataset.giftMinTier);
        gift.classList.toggle('is-unlocked', unlocked);
        gift.classList.toggle('is-locked', !unlocked);
        gift.dataset.giftUnlocked = String(unlocked);
        gift.querySelector('[data-gift-locked-copy]').hidden = unlocked;
        gift.querySelector('[data-gift-status]').hidden = !unlocked;
        if (unlocked) unlockedCount += 1;
      });
      if (priorTier !== undefined && priorTier !== tier) {
        const announcement = supply.querySelector('[data-dv-gift-announcement]');
        if (announcement) announcement.textContent = `${selected.dataset.packTitle}: ${unlockedCount} ${unlockedCount === 1 ? 'regalo incluido' : 'regalos incluidos'}`;
      }
      priorTier = tier;
      if (stickyButton) {
        const ready = detail?.ready ?? (typeof root.isReady === 'function' && root.isReady());
        stickyButton.textContent = root.dataset.dvRitualLabel || `Añadir ${selected.dataset.packTitle} (${selected.dataset.packTotal}) →`;
        stickyButton.setAttribute('aria-disabled', String(!ready));
        if ('disabled' in stickyButton) stickyButton.disabled = !ready;
      }
    };
    root.addEventListener('dear-v:pack-change', event => {
      if (event.target === root) update(event.detail);
    }, { signal: abort.signal });
    stickyButton?.addEventListener('click', event => {
      event.preventDefault();
      if (typeof root.isReady !== 'function' || !root.isReady()) return;
      const form = root.querySelector('product-form-component form');
      const submitter = form?.querySelector('[ref="addToCartButton"]');
      if (form && submitter && !submitter.disabled) form.requestSubmit(submitter);
    }, { signal: abort.signal });
    instances.set(root, abort);
    update();
  }

  function scan(scope = document) {
    refreshDelivery(scope);
    if (scope.matches?.('mte-product-component')) initialize(scope);
    scope.querySelectorAll?.('mte-product-component').forEach(initialize);
  }

  function remove(scope) {
    for (const [root, abort] of instances) {
      if (scope === root || scope.contains(root)) {
        abort.abort();
        instances.delete(root);
      }
    }
  }

  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => remove(event.target));
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshDelivery(); });
  window.addEventListener('pageshow', () => scan(document));
  window.addEventListener('pagehide', () => remove(document));
  window.DearVR17Buybox = { version: 'r17', scan, deliveryDates };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
  customElements.whenDefined('mte-product-component').then(() => scan(document));
})();


(() => {
  if (window.DearVR17Widgets) return;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  const countupFrames = new Map();
  const completedCounts = new WeakSet();
  let countObserver;
  const root = () => document.querySelector('main[data-dear-v-format]') || document.querySelector('#MainContent');

  function mountChecklist(section) {
    if (instances.has(section)) return;
    const controller = new AbortController();
    const options = Array.from(section.querySelectorAll('.dv-check-item'));
    const panel = section.querySelector('.dv-check-result');
    const count = section.querySelector('[data-dv-check-count]');
    options.forEach(button => button.addEventListener('click', () => {
      button.setAttribute('aria-pressed', String(button.getAttribute('aria-pressed') !== 'true'));
      const selected = options.filter(option => option.getAttribute('aria-pressed') === 'true').length;
      count.textContent = String(selected);
      panel.hidden = selected === 0;
    }, { signal: controller.signal }));
    section.querySelector('[data-dv-recommend-pack]')?.addEventListener('click', () => {
      const product = root()?.querySelector('mte-product-component[data-isolated-funnel="true"]');
      if (!product) return;
      const radio = product.querySelector('[data-mte-supply] [data-tier="3"] input[type="radio"]');
      if (radio) radio.click();
      else product.dispatchEvent(new CustomEvent('dear-v:select-pack', { bubbles: true, detail: { pack: '3' } }));
      const anchor = product.querySelector('#mte-buy-box') || product;
      anchor.scrollIntoView({ behavior: motion.matches ? 'instant' : 'smooth', block: 'start' });
      radio?.focus({ preventScroll: true });
    }, { signal: controller.signal });
    instances.set(section, () => controller.abort());
  }

  function mountReviews(section) {
    if (instances.has(section)) return;
    const controller = new AbortController();
    const cards = Array.from(section.querySelectorAll('[data-r6-review-card]'));
    const buttons = Array.from(section.querySelectorAll('[data-dv-r17-review-filter]'));
    const count = section.querySelector('[data-r6-review-count]');
    const empty = section.querySelector('[data-r6-review-empty]');
    const animations = new Map();
    function filter(topic, animate = true) {
      let shown = 0;
      for (const card of cards) {
        animations.get(card)?.cancel();
        animations.delete(card);
        const topics = (card.dataset.r6ReviewTopics || '').split(',').map(value => value.trim());
        const matches = topic === 'all' || topics.includes(topic);
        card.hidden = !matches;
        if (matches) {
          shown++;
          if (animate && !motion.matches && typeof card.animate === 'function') {
            const animation = card.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 260, easing: 'ease-out' });
            animations.set(card, animation);
            animation.onfinish = () => animations.delete(card);
          }
        }
      }
      buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.dvR17ReviewFilter === topic)));
      if (count) count.textContent = String(shown);
      if (empty) empty.hidden = shown !== 0;
      section.dataset.activeFilter = topic;
    }
    buttons.forEach(button => button.addEventListener('click', () => filter(button.dataset.dvR17ReviewFilter), { signal: controller.signal }));
    filter('all', false);
    instances.set(section, () => { controller.abort(); animations.forEach(animation => animation.cancel()); });
  }

  function animateCount(element) {
    if (completedCounts.has(element)) return;
    completedCounts.add(element);
    const target = Number(element.dataset.dvR17Countup);
    if (!Number.isFinite(target)) return;
    const finalValue = `${target}%`;
    element.setAttribute('aria-label', finalValue);
    if (motion.matches) { element.textContent = finalValue; return; }
    let started;
    const tick = now => {
      started ??= now;
      const progress = Math.min((now - started) / 900, 1);
      element.textContent = `${Math.round(target * (1 - Math.pow(1 - progress, 3)))}%`;
      if (progress < 1) countupFrames.set(element, requestAnimationFrame(tick));
      else countupFrames.delete(element);
    };
    element.textContent = '0%';
    countupFrames.set(element, requestAnimationFrame(tick));
  }

  function mountCounts(scope) {
    // Existing source percentages stay unchanged until their first intersection.
    // Leaf selection avoids replacing a paragraph, icon, or its accessible text.
    const candidates = Array.from(scope.querySelectorAll('span,strong,div,p')).filter(element => !element.children.length && /^(94|96|91)%$/.test(element.textContent.trim()));
    if ('IntersectionObserver' in window && !motion.matches) {
      countObserver ??= new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) { animateCount(entry.target); countObserver.unobserve(entry.target); }
      }), { threshold: .25 });
    }
    candidates.forEach(element => {
      if (completedCounts.has(element)) return;
      element.dataset.dvR17Countup ||= element.textContent.trim().replace('%', '');
      if (countObserver && !motion.matches) countObserver.observe(element);
      else animateCount(element);
    });
  }

  function mount() {
    const main = root();
    if (!main?.querySelector('[data-dv-for-you]')) return;
    main.querySelectorAll('[data-dv-for-you]').forEach(mountChecklist);
    main.querySelectorAll('[data-dv-r17-reviews]').forEach(mountReviews);
    mountCounts(main);
  }
  function destroy(scope = document) {
    for (const [element, dispose] of instances) if (scope === document || scope.contains(element)) { dispose(); instances.delete(element); }
    for (const [element, frame] of countupFrames) if (scope === document || scope.contains(element)) { cancelAnimationFrame(frame); element.textContent = `${element.dataset.dvR17Countup}%`; countupFrames.delete(element); }
    if (scope === document) { countObserver?.disconnect(); countObserver = undefined; }
  }
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  window.addEventListener('pagehide', () => destroy());
  window.addEventListener('pageshow', mount);
  motion.addEventListener('change', () => {
    if (motion.matches) {
      countupFrames.forEach((frame, element) => { cancelAnimationFrame(frame); element.textContent = `${element.dataset.dvR17Countup}%`; });
      countupFrames.clear();
    }
  });
  window.DearVR17Widgets = { mount, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
})();

(() => {
  if (window.DearVR177Autotest) { window.DearVR177Autotest.mount(); return; }
  const instances = new Map();
  function initialize(root) {
    if (instances.has(root)) return;
    const items = Array.from(root.querySelectorAll('.dv-autotest-item[role="checkbox"]'));
    const hint = root.querySelector('.dv-autotest-hint');
    const message = root.querySelector('[data-dv-autotest-message]');
    const cta = root.querySelector('.dv-autotest-cta');
    if (!items.length || !hint || !message || !cta) return;
    const controller = new AbortController();
    const update = () => {
      const count = items.filter(item => item.getAttribute('aria-checked') === 'true').length;
      root.dataset.selectedCount = String(count);
      hint.textContent = `Toca las situaciones que te ocurren (${count}/4 seleccionadas):`;
      cta.hidden = count === 0;
      if (count === 0) {
        message.textContent = 'Marca al menos 1 casilla para ver tu recomendación clínica.';
      } else if (count === 1) {
        message.textContent = '✓ 1 de 4 marcadas — Tu flora sufre desajustes puntuales. Con 1 toma diaria mantienes tu pH blindado.';
        cta.textContent = 'Ver mi pack recomendado ↑';
      } else {
        message.textContent = `✓ ${count} de 4 marcadas — Prioridad alta de equilibrio íntimo: Te recomendamos el Ritual de 3 Meses (90 días) para estabilizar tu microbiota de raíz.`;
        cta.textContent = 'Elegir Ritual 3 Meses (Ahorra 24,80 €) ↑';
      }
    };
    const reset = () => {
      items.forEach(item => { item.classList.remove('is-checked'); item.setAttribute('aria-checked', 'false'); });
      update();
    };
    items.forEach(item => item.addEventListener('click', () => {
      const checked = item.getAttribute('aria-checked') !== 'true';
      item.setAttribute('aria-checked', String(checked));
      item.classList.toggle('is-checked', checked);
      update();
    }, { signal: controller.signal }));
    instances.set(root, { controller, reset });
    reset();
  }
  function mount() {
    document.querySelectorAll('body:is(.mte-page--just-one,.mte-page--pineapple-crush) [data-dv-autotest]').forEach(initialize);
  }
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, state] of instances) if (event.target.contains(root)) { state.controller.abort(); instances.delete(root); }
  });
  window.addEventListener('pageshow', event => {
    mount();
    if (event.persisted) instances.forEach(state => state.reset());
  });
  window.DearVR177Autotest = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, { once: true });
  else mount();
})();

(() => {
  if (window.DearVR177MechScenes) {
    window.DearVR177MechScenes.scan(document);
    return;
  }
  const selector = 'body.mte-page--just-one #MainContent .dv-mech-grid, body.mte-page--pineapple-crush #MainContent .dv-mech-grid';
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();

  function syncVisibility(state) {
    state.cards.forEach(card => card.classList.toggle('is-mech-inview', !document.hidden && state.visible.has(card)));
  }

  function configure(grid, state) {
    state.observer?.disconnect();
    state.observer = null;
    state.visible.clear();
    state.cards.forEach(card => card.classList.remove('is-mech-inview'));
    if (motion.matches || !('IntersectionObserver' in window)) {
      grid.classList.remove('is-mech-ready');
      state.cards.forEach(card => card.classList.add('is-mech-active'));
      return;
    }
    try {
      state.observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) state.visible.add(entry.target);
          else state.visible.delete(entry.target);
          if (entry.isIntersecting && entry.intersectionRatio >= .15) entry.target.classList.add('is-mech-active');
        }
        syncVisibility(state);
      }, { threshold: [0, .15], rootMargin: '-4% 0px -4% 0px' });
      grid.classList.add('is-mech-ready');
      state.cards.forEach(card => state.observer.observe(card));
    } catch {
      state.observer?.disconnect();
      state.observer = null;
      grid.classList.remove('is-mech-ready');
      state.cards.forEach(card => card.classList.add('is-mech-active'));
    }
  }

  function dispose(grid, state) {
    state.observer?.disconnect();
    grid.classList.remove('is-mech-ready');
    state.cards.forEach(card => card.classList.remove('is-mech-active', 'is-mech-inview'));
    instances.delete(grid);
  }

  function scan(scope = document) {
    for (const [grid, state] of instances) if (!grid.isConnected) dispose(grid, state);
    const grids = [...(scope.matches?.(selector) ? [scope] : []), ...(scope.querySelectorAll?.(selector) || [])];
    grids.forEach(grid => {
      if (instances.has(grid)) return;
      const cards = Array.from(grid.querySelectorAll('.dv-mech-card')).filter(card => card.closest('.dv-mech-grid') === grid);
      if (!cards.length) return;
      const state = { cards, visible: new Set(), observer: null };
      instances.set(grid, state);
      configure(grid, state);
    });
  }

  function destroy(scope = document) {
    for (const [grid, state] of instances) if (scope === document || scope === grid || scope.contains?.(grid)) dispose(grid, state);
  }

  document.addEventListener('visibilitychange', () => instances.forEach(syncVisibility));
  const onMotionChange = () => instances.forEach((state, grid) => configure(grid, state));
  if (motion.addEventListener) motion.addEventListener('change', onMotionChange);
  else motion.addListener(onMotionChange);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  window.DearVR177MechScenes = { scan, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
})();

/* Dear V · Mobile proof carousel · 2026-10-17 */
(() => {
  if (window.DearVProofCarousel) { window.DearVProofCarousel.scan(document); return; }
  const selector = '.dv-proof-carousel';
  const instances = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initialize(section) {
    if (instances.has(section)) return;
    const track = section.querySelector('[data-proof-track]');
    const cards = track ? Array.from(track.children).filter(card => card.matches('.dv-proof-card')) : [];
    if (!track || !cards.length) return;
    const controller = new AbortController();
    const listen = (element, event, callback, options = {}) => element?.addEventListener(event, callback, { ...options, signal: controller.signal });
    const dots = Array.from(section.querySelectorAll('[data-proof-goto]'));
    const counter = section.querySelector('[data-proof-current]');
    const previous = section.querySelector('[data-proof-prev]');
    const next = section.querySelector('[data-proof-next]');
    let frame = 0;
    const maximum = () => Math.max(0, track.scrollWidth - track.clientWidth);
    const position = card => Math.max(0, Math.min(maximum(), card.getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft - track.clientLeft - (parseFloat(getComputedStyle(track).paddingLeft) || 0)));
    function activeIndex() {
      const max = maximum();
      if (max < 2 || track.scrollLeft < 1) return 0;
      if (track.scrollLeft >= max - 2) return cards.length - 1;
      let closest = 0, distance = Infinity;
      cards.forEach((card, index) => {
        const delta = Math.abs(position(card) - track.scrollLeft);
        if (delta < distance) { closest = index; distance = delta; }
      });
      return closest;
    }
    function sync() {
      frame = 0;
      const index = activeIndex();
      if (counter) counter.textContent = String(index + 1).padStart(2, '0');
      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === index);
        dot.setAttribute('aria-selected', String(dotIndex === index));
        dot.tabIndex = dotIndex === index ? 0 : -1;
      });
      previous?.setAttribute('aria-disabled', String(maximum() < 2 || index === 0));
      next?.setAttribute('aria-disabled', String(maximum() < 2 || index === cards.length - 1));
    }
    function schedule() { if (!frame) frame = requestAnimationFrame(sync); }
    function goTo(index, instant = false) {
      const card = cards[Math.max(0, Math.min(cards.length - 1, index))];
      track.scrollTo({ left: position(card), behavior: instant || motion.matches ? 'instant' : 'smooth' });
      schedule();
    }
    listen(previous, 'click', () => goTo(activeIndex() - 1));
    listen(next, 'click', () => goTo(activeIndex() + 1));
    dots.forEach((dot, index) => {
      listen(dot, 'click', () => goTo(index));
      listen(dot, 'keydown', event => {
        let target;
        if (event.key === 'ArrowRight') target = Math.min(cards.length - 1, index + 1);
        if (event.key === 'ArrowLeft') target = Math.max(0, index - 1);
        if (event.key === 'Home') target = 0;
        if (event.key === 'End') target = cards.length - 1;
        if (target === undefined) return;
        event.preventDefault(); dots[target]?.focus({ preventScroll: true }); goTo(target);
      });
    });
    listen(track, 'scroll', schedule, { passive: true });
    listen(track, 'keydown', event => {
      if (event.target !== track) return;
      let target;
      if (event.key === 'ArrowRight') target = activeIndex() + 1;
      if (event.key === 'ArrowLeft') target = activeIndex() - 1;
      if (event.key === 'Home') target = 0;
      if (event.key === 'End') target = cards.length - 1;
      if (target === undefined) return;
      event.preventDefault(); goTo(target);
    });
    const resize = 'ResizeObserver' in window ? new ResizeObserver(schedule) : null;
    if (resize) resize.observe(track);
    else listen(window, 'resize', schedule, { passive: true });
    listen(section, 'shopify:block:select', event => {
      const index = cards.findIndex(card => card.dataset.mteBlock === event.detail?.blockId);
      if (index >= 0) goTo(index, true);
    });
    instances.set(section, () => { controller.abort(); resize?.disconnect(); cancelAnimationFrame(frame); instances.delete(section); });
    sync();
  }
  function scan(scope = document) {
    for (const [section, destroy] of instances) if (!section.isConnected) destroy();
    if (scope.matches?.(selector)) initialize(scope);
    scope.querySelectorAll?.(selector).forEach(initialize);
  }
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [section, destroy] of instances) if (event.target.contains(section)) destroy();
  });
  window.addEventListener('pagehide', () => { for (const destroy of instances.values()) destroy(); });
  window.addEventListener('pageshow', () => scan(document));
  window.DearVProofCarousel = { scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(document), { once: true });
  else scan(document);
})();

(() => {
  if (window.DearVGalleryLightbox) {
    window.DearVGalleryLightbox.mount(document);
    return;
  }
  const mounted = new WeakSet();
  let lightboxEl = null;
  let lightboxState = null;

  function ensureLightbox() {
    if (lightboxEl && lightboxEl.isConnected) return lightboxEl;
    const modal = document.createElement('div');
    modal.className = 'dv-lightbox';
    modal.dataset.dvLightbox = '';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Visor de fotos ampliadas');
    modal.innerHTML = `
      <div class="dv-lightbox__backdrop" data-dv-lb-close></div>
      <div class="dv-lightbox__topbar">
        <div class="dv-lightbox__badge-wrap">
          <span class="dv-lightbox__counter" data-dv-lb-counter>1/6</span>
          <span class="dv-lightbox__title" data-dv-lb-title>Fórmula Clínica UE</span>
        </div>
        <div class="dv-lightbox__actions">
          <button type="button" class="dv-lightbox__zoom-pill" data-dv-lb-zoom aria-label="Alternar zoom">
            <svg width="14" height="14" viewBox="0 0 22 22" fill="none" aria-hidden="true">
              <circle cx="9.8" cy="9.8" r="6.5" stroke="currentColor" stroke-width="2"/>
              <path d="M9.8 7V12.6M7 9.8H12.6M14.5 14.5L19 19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span data-dv-lb-zoom-label>2.2x</span>
          </button>
          <button type="button" class="dv-lightbox__close" data-dv-lb-close aria-label="Cerrar vista ampliada">✕</button>
        </div>
      </div>
      <div class="dv-lightbox__viewport" data-dv-lb-viewport>
        <div class="dv-lightbox__canvas" data-dv-lb-canvas>
          <img class="dv-lightbox__img" data-dv-lb-img src="" alt="" draggable="false">
          <div class="dv-lightbox__progress" aria-hidden="true">
            <span class="dv-lightbox__progress-bar" data-dv-lb-progress></span>
          </div>
        </div>
        <div class="dv-lightbox__controls">
          <button type="button" class="dv-lightbox__nav dv-lightbox__nav--prev" data-dv-lb-prev aria-label="Imagen anterior">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M12.5 4.5L7 10L12.5 15.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <div class="dv-lightbox__hint" data-dv-lb-hint>Toca 2 veces o pellizca la foto para hacer zoom</div>
          <button type="button" class="dv-lightbox__nav dv-lightbox__nav--next" data-dv-lb-next aria-label="Imagen siguiente">
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M7.5 4.5L13 10L7.5 15.5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </div>
      </div>
      <div class="dv-lightbox__thumbs" data-dv-lb-thumbs role="tablist" aria-label="Miniaturas de galería"></div>
    `;
    document.body.appendChild(modal);
    lightboxEl = modal;

    const img = modal.querySelector('[data-dv-lb-img]');
    const canvas = modal.querySelector('[data-dv-lb-canvas]');
    const counter = modal.querySelector('[data-dv-lb-counter]');
    const titleEl = modal.querySelector('[data-dv-lb-title]');
    const progressEl = modal.querySelector('[data-dv-lb-progress]');
    const zoomBtn = modal.querySelector('[data-dv-lb-zoom]');
    const zoomLabel = modal.querySelector('[data-dv-lb-zoom-label]');
    const hintEl = modal.querySelector('[data-dv-lb-hint]');
    const thumbsWrap = modal.querySelector('[data-dv-lb-thumbs]');

    lightboxState = {
      slides: [],
      index: 0,
      scale: 1,
      tx: 0,
      ty: 0,
      onSyncPage: null,
      applyTransform() {
        img.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
        const zoomed = this.scale > 1.05;
        modal.classList.toggle('is-zoomed', zoomed);
        if (zoomLabel) zoomLabel.textContent = zoomed ? '1x' : '2.2x';
        if (hintEl) hintEl.textContent = zoomed ? 'Arrastra con el dedo para explorar · Toca 2 veces para salir' : 'Toca 2 veces o pellizca la foto para hacer zoom';
      },
      resetZoom() {
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.applyTransform();
      },
      toggleZoom(originX = null, originY = null) {
        if (this.scale > 1.05) {
          this.resetZoom();
        } else {
          this.scale = 2.2;
          if (originX !== null && originY !== null) {
            const rect = canvas.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            this.tx = Math.max(-165, Math.min(165, (cx - originX) * 0.85));
            this.ty = Math.max(-165, Math.min(165, (cy - originY) * 0.85));
          } else {
            this.tx = 0;
            this.ty = 0;
          }
          this.applyTransform();
        }
      },
      show(idx, animate = true) {
        if (!this.slides.length) return;
        this.index = (idx + this.slides.length) % this.slides.length;
        this.resetZoom();
        const slide = this.slides[this.index];
        if (animate) {
          img.classList.remove('is-switching');
          void img.offsetWidth;
          img.classList.add('is-switching');
        }
        img.src = slide.src;
        img.alt = slide.alt || '';
        if (counter) counter.textContent = `${this.index + 1}/${this.slides.length}`;
        if (titleEl) {
          titleEl.textContent = slide.title || '';
          titleEl.hidden = !slide.title;
        }
        if (progressEl) {
          progressEl.style.width = `${((this.index + 1) / this.slides.length) * 100}%`;
        }
        Array.from(thumbsWrap.children).forEach((btn, i) => {
          const active = i === this.index;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-selected', String(active));
          if (active && btn.scrollIntoView) {
            btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          }
        });
        if (typeof this.onSyncPage === 'function') this.onSyncPage(this.index);
      },
      open(slides, startIndex, onSyncPage) {
        this.slides = slides;
        this.onSyncPage = onSyncPage;
        const isPineapple = document.body.classList.contains('mte-page--pineapple-crush');
        modal.classList.toggle('dv-lightbox--pineapple-crush', isPineapple);
        modal.classList.toggle('dv-lightbox--just-one', !isPineapple);
        thumbsWrap.replaceChildren();
        slides.forEach((slide, i) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'dv-lightbox__thumb';
          btn.setAttribute('role', 'tab');
          btn.setAttribute('aria-label', slide.title ? `${slide.title} (${i + 1})` : `Imagen ${i + 1}`);
          const tImg = document.createElement('img');
          tImg.src = slide.src;
          tImg.alt = '';
          btn.appendChild(tImg);
          btn.addEventListener('click', () => this.show(i, true));
          thumbsWrap.appendChild(btn);
        });
        modal.hidden = false;
        document.documentElement.classList.add('dv-lightbox-open');
        this.show(startIndex, false);
      },
      close() {
        modal.hidden = true;
        document.documentElement.classList.remove('dv-lightbox-open');
        this.resetZoom();
      }
    };

    modal.querySelectorAll('[data-dv-lb-close]').forEach(el => el.addEventListener('click', () => lightboxState.close()));
    modal.querySelector('[data-dv-lb-prev]')?.addEventListener('click', () => lightboxState.show(lightboxState.index - 1, true));
    modal.querySelector('[data-dv-lb-next]')?.addEventListener('click', () => lightboxState.show(lightboxState.index + 1, true));
    zoomBtn?.addEventListener('click', () => lightboxState.toggleZoom());

    let lastTap = 0;
    let dragStart = null;
    let pinchStartDist = 0;
    let pinchStartScale = 1;

    canvas.addEventListener('touchstart', e => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchStartDist = Math.hypot(dx, dy);
        pinchStartScale = lightboxState.scale;
        dragStart = null;
      } else if (e.touches.length === 1) {
        dragStart = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          tx: lightboxState.tx,
          ty: lightboxState.ty,
          moved: false
        };
      }
    }, { passive: true });

    canvas.addEventListener('touchmove', e => {
      if (e.touches.length === 2 && pinchStartDist > 0) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        lightboxState.scale = Math.max(1, Math.min(3, pinchStartScale * (dist / pinchStartDist)));
        if (lightboxState.scale === 1) { lightboxState.tx = 0; lightboxState.ty = 0; }
        lightboxState.applyTransform();
      } else if (e.touches.length === 1 && dragStart) {
        const dx = e.touches[0].clientX - dragStart.x;
        const dy = e.touches[0].clientY - dragStart.y;
        if (Math.abs(dx) > 6 || Math.abs(dy) > 6) dragStart.moved = true;
        if (lightboxState.scale > 1.05) {
          e.preventDefault();
          const maxPan = (lightboxState.scale - 1) * 165;
          lightboxState.tx = Math.max(-maxPan, Math.min(maxPan, dragStart.tx + dx));
          lightboxState.ty = Math.max(-maxPan, Math.min(maxPan, dragStart.ty + dy));
          lightboxState.applyTransform();
        }
      }
    }, { passive: false });

    canvas.addEventListener('touchend', e => {
      if (e.touches.length === 0 && dragStart) {
        const touch = e.changedTouches[0];
        const dx = touch.clientX - dragStart.x;
        const dy = touch.clientY - dragStart.y;
        if (!dragStart.moved) {
          const now = Date.now();
          if (now - lastTap < 320) {
            lightboxState.toggleZoom(touch.clientX, touch.clientY);
            lastTap = 0;
          } else {
            lastTap = now;
          }
        } else if (lightboxState.scale <= 1.05 && Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.25) {
          lightboxState.show(lightboxState.index + (dx < 0 ? 1 : -1), true);
        }
        dragStart = null;
        pinchStartDist = 0;
      }
    }, { passive: true });

    canvas.addEventListener('dblclick', e => {
      e.preventDefault();
      lightboxState.toggleZoom(e.clientX, e.clientY);
    });

    window.addEventListener('keydown', e => {
      if (modal.hidden) return;
      if (e.key === 'Escape') lightboxState.close();
      if (e.key === 'ArrowRight') lightboxState.show(lightboxState.index + 1, true);
      if (e.key === 'ArrowLeft') lightboxState.show(lightboxState.index - 1, true);
    });

    return modal;
  }

  function mount(scope = document) {
    const galleries = [...(scope.matches?.('[data-dv-gallery]') ? [scope] : []), ...scope.querySelectorAll('[data-dv-gallery]')];
    galleries.forEach(gallery => {
      if (mounted.has(gallery)) return;
      const track = gallery.querySelector('[data-dv-stage-track]');
      const main = gallery.querySelector('[data-dv-main-image]');
      const counter = gallery.querySelector('[data-dv-gallery-counter-text]') || gallery.querySelector('[data-dv-gallery-counter]');
      const progress = gallery.querySelector('[data-dv-gallery-progress]');
      const slidesEls = Array.from(gallery.querySelectorAll('.dv-stage-slide'));
      const thumbs = Array.from(gallery.querySelectorAll('[data-dv-thumb]'));
      if (!thumbs.length) return;
      mounted.add(gallery);
      let currentIndex = 0;
      let scrollFrame = 0;

      const syncThumbs = index => {
        currentIndex = index;
        if (counter) counter.textContent = `${index + 1} / ${thumbs.length}`;
        if (progress) progress.style.width = `${((index + 1) / thumbs.length) * 100}%`;
        slidesEls.forEach((slideEl, i) => slideEl.classList.toggle('is-active', i === index));
        thumbs.forEach((thumb, i) => {
          thumb.classList.toggle('is-active', i === index);
          thumb.setAttribute('aria-selected', String(i === index));
          thumb.tabIndex = i === index ? 0 : -1;
        });
      };

      const activate = (index, scrollTrack = true) => {
        const targetIndex = ((index % thumbs.length) + thumbs.length) % thumbs.length;
        const button = thumbs[targetIndex];
        if (!button) return;
        syncThumbs(targetIndex);
        if (track && scrollTrack) {
          const targetSlide = slidesEls[targetIndex];
          if (targetSlide) {
            track.scrollTo({
              left: targetSlide.offsetLeft - track.offsetLeft,
              behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'
            });
          }
        } else if (main) {
          main.src = button.dataset.dvThumb;
          main.alt = button.dataset.dvAlt || '';
        }
      };

      gallery.querySelector('[data-dv-stage-prev]')?.addEventListener('click', () => activate(currentIndex - 1));
      gallery.querySelector('[data-dv-stage-next]')?.addEventListener('click', () => activate(currentIndex + 1));

      if (track) {
        track.addEventListener('scroll', () => {
          if (scrollFrame) cancelAnimationFrame(scrollFrame);
          scrollFrame = requestAnimationFrame(() => {
            const width = track.clientWidth;
            if (width > 0) {
              const idx = Math.max(0, Math.min(thumbs.length - 1, Math.round(track.scrollLeft / width)));
              syncThumbs(idx);
            }
          });
        }, { passive: true });
      }

      thumbs.forEach((button, index) => {
        button.tabIndex = index ? -1 : 0;
        button.addEventListener('click', () => activate(index));
        button.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % thumbs.length;
          if (event.key === 'ArrowLeft') next = (index + thumbs.length - 1) % thumbs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = thumbs.length - 1;
          if (next === undefined) return;
          event.preventDefault();
          activate(next);
          thumbs[next].focus();
        });
      });

      const openGalleryZoom = startIdx => {
        ensureLightbox();
        const slides = thumbs.map((btn, i) => ({
          src: btn.dataset.dvThumb,
          alt: btn.dataset.dvAlt || '',
          title: btn.dataset.dvTitle || slidesEls[i]?.dataset.dvTitle || ''
        }));
        lightboxState.open(slides, startIdx ?? currentIndex, idx => activate(idx));
      };

      gallery.querySelector('[data-dv-zoom-trigger]')?.addEventListener('click', () => openGalleryZoom(currentIndex));
      gallery.querySelectorAll('[data-dv-zoomable-img]').forEach(imgEl => {
        imgEl.addEventListener('click', () => openGalleryZoom(Number(imgEl.dataset.dvZoomableImg) || currentIndex));
      });
    });

    const mteGalleries = [...(scope.matches?.('.mte-gallery') ? [scope] : []), ...scope.querySelectorAll('.mte-gallery')];
    mteGalleries.forEach(galleryWrap => {
      if (galleryWrap.querySelector('[data-dv-gallery]')) return;
      const r6Grid = galleryWrap.querySelector('.dear-v-r6-gallery[data-mte-gallery="product"]');
      if (!r6Grid || mounted.has(r6Grid)) return;
      mounted.add(r6Grid);
      const productComp = galleryWrap.closest('mte-product-component');
      const r6Counter = galleryWrap.querySelector('[data-dv-gallery-counter-text]') || galleryWrap.querySelector('[data-dv-gallery-counter]');
      const r6Progress = galleryWrap.querySelector('[data-dv-gallery-progress]');
      const r6Tiles = Array.from(r6Grid.querySelectorAll('.image-tile'));
      let r6ScrollFrame = 0;
      if (r6Tiles.length) {
        r6Grid.addEventListener('scroll', () => {
          if (r6ScrollFrame) cancelAnimationFrame(r6ScrollFrame);
          r6ScrollFrame = requestAnimationFrame(() => {
            const width = r6Tiles[0]?.getBoundingClientRect().width || r6Grid.clientWidth;
            if (width > 0) {
              const idx = Math.max(0, Math.min(r6Tiles.length - 1, Math.round(r6Grid.scrollLeft / width)));
              if (r6Counter) r6Counter.textContent = `${idx + 1} / ${r6Tiles.length}`;
              if (r6Progress) r6Progress.style.width = `${((idx + 1) / r6Tiles.length) * 100}%`;
              r6Tiles.forEach((tile, i) => tile.classList.toggle('is-active', i === idx));
            }
          });
        }, { passive: true });
      }
      const openR6Zoom = startIdx => {
        ensureLightbox();
        const imgs = Array.from(r6Grid.querySelectorAll('.image-tile img'));
        const slides = imgs.map((img, i) => ({
          src: img.currentSrc || img.src,
          alt: img.alt || '',
          title: img.dataset.dvTitle || r6Tiles[i]?.dataset.dvTitle || ''
        }));
        const activeIdx = startIdx ?? (productComp?.state?.galleryIndex || 0);
        lightboxState.open(slides, activeIdx, idx => productComp?.goToMedia?.(idx));
      };
      galleryWrap.querySelector('[data-dv-zoom-trigger]')?.addEventListener('click', () => openR6Zoom());
      r6Grid.querySelectorAll('[data-dv-zoomable-img]').forEach(imgEl => {
        imgEl.addEventListener('click', () => openR6Zoom(Number(imgEl.dataset.dvZoomableImg) || 0));
      });
    });

    scope.querySelectorAll('[data-dv-pack-scroll]').forEach(button => {
      if (mounted.has(button)) return;
      mounted.add(button);
      button.addEventListener('click', event => {
        const target = document.getElementById('mte-buy-box');
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
        const input = target.querySelector('input:checked');
        input?.focus({ preventScroll: true });
      });
    });

    const disruptorSections = [...(scope.matches?.('[data-dv-disruptors]') ? [scope] : []), ...scope.querySelectorAll('[data-dv-disruptors]')];
    disruptorSections.forEach(section => {
      if (mounted.has(section)) return;
      mounted.add(section);
      const viewport = section.querySelector('[data-dv-hz-viewport]') || section;
      const track = section.querySelector('[data-dv-hz-track]');
      const cards = Array.from(section.querySelectorAll('[data-dv-disruptor-card]'));
      const tabs = Array.from(section.querySelectorAll('[data-dv-hz-tab]'));
      const dots = Array.from(section.querySelectorAll('[data-dv-hz-dot]'));
      const scrubber = section.querySelector('[data-dv-hz-scrubber]');
      const prevBtn = section.querySelector('[data-dv-hz-prev]');
      const nextBtn = section.querySelector('[data-dv-hz-next]');
      const counterEl = section.querySelector('[data-dv-hz-counter]');
      const pin = section.querySelector('[data-dv-ph-pin]');
      const valEl = section.querySelector('[data-dv-ph-val]');
      const idxEl = section.querySelector('[data-dv-ph-index]');
      const impactEl = section.querySelector('[data-dv-ph-impact]');
      if (!track || !cards.length) return;

      const N = cards.length;
      const cardParts = cards.map(card => ({
        card,
        media: card.querySelector('[data-dv-card-media]') || card.querySelector('.dv-disruptor-card__media'),
        content: card.querySelector('[data-dv-card-content]') || card.querySelector('.dv-disruptor-card__content'),
        img: card.querySelector('[data-dv-card-img]') || card.querySelector('.dv-disruptor-card__img')
      }));

      let currentP = 0;
      let targetP = 0;
      let velocityP = 0;
      let lastTickTime = 0;
      let activeIndex = -1;
      let rafId = 0;
      let isDirectInput = false;
      let metrics = null;
      const lastW = new Float32Array(N).fill(-1);

      const clampVal = (v, min, max) => Math.max(min, Math.min(max, v));
      const smoothstep = t => t * t * (3 - 2 * t);

      const computeMetrics = () => {
        const vw = viewport.clientWidth || section.clientWidth || 360;
        let gap, wClosed, wOpen, wMediaOpen, wContent, imgSize, totalStrip;
        if (vw >= 920) {
          gap = 12;
          const totalGaps = (N - 1) * gap;
          const usable = Math.max(640, vw - totalGaps - 4);
          wClosed = Math.round(usable * 0.106);
          wOpen = usable - (N - 1) * wClosed;
          wMediaOpen = Math.round(wOpen * 0.48);
          wContent = wOpen - wMediaOpen;
          imgSize = 292;
          totalStrip = usable + totalGaps + 4;
        } else if (vw >= 750) {
          gap = 10;
          wClosed = 92;
          wOpen = Math.min(430, Math.round(vw * 0.62));
          wMediaOpen = Math.round(wOpen * 0.47);
          wContent = wOpen - wMediaOpen;
          imgSize = 292;
          totalStrip = (N - 1) * wClosed + wOpen + (N - 1) * gap + 4;
        } else {
          gap = 8;
          wClosed = 68;
          wOpen = Math.min(296, Math.max(256, vw - 74));
          wMediaOpen = Math.round(wOpen * 0.455);
          wContent = wOpen - wMediaOpen;
          imgSize = 208;
          totalStrip = (N - 1) * wClosed + wOpen + (N - 1) * gap + 4;
        }

        const tabParent = tabs[0]?.parentElement || null;
        const tabParentW = tabParent ? tabParent.clientWidth : 0;
        const tabTargets = tabs.map(tab => {
          if (!tabParentW) return 0;
          return Math.max(0, tab.offsetLeft - (tabParentW - tab.clientWidth) * 0.5);
        });

        metrics = { vw, gap, wClosed, wOpen, wMediaOpen, wContent, imgSize, totalStrip, tabParent, tabTargets };

        const contentWStr = `${wContent}px`;
        const imgWStr = `${imgSize}px`;
        for (let i = 0; i < N; i++) {
          const { content, img } = cardParts[i];
          if (content) {
            content.style.width = contentWStr;
            content.style.minWidth = contentWStr;
            content.style.maxWidth = contentWStr;
            content.style.flexBasis = contentWStr;
          }
          if (img) {
            img.style.width = imgWStr;
          }
        }
        lastW.fill(-1);
      };

      const syncActiveMeta = (idx, scrollTab = true) => {
        if (idx === activeIndex) return;
        activeIndex = idx;
        const activeCard = cards[idx];
        for (let i = 0; i < N; i++) {
          const isSelected = i === idx;
          cards[i].classList.toggle('is-active', isSelected);
          cards[i].setAttribute('aria-pressed', String(isSelected));
          cards[i].setAttribute('aria-expanded', String(isSelected));
          if (tabs[i]) {
            tabs[i].classList.toggle('is-active', isSelected);
            tabs[i].setAttribute('aria-selected', String(isSelected));
          }
          if (dots[i]) {
            dots[i].classList.toggle('is-active', isSelected);
          }
        }
        if (scrollTab && metrics?.tabParent && metrics.tabTargets[idx] !== undefined) {
          metrics.tabParent.scrollLeft = metrics.tabTargets[idx];
        }
        if (counterEl) {
          counterEl.textContent = `0${idx + 1} / 0${N}`;
        }
        if (activeCard) {
          if (pin && activeCard.dataset.disruptorPos) {
            pin.style.setProperty('--dv-ph-pos', `${activeCard.dataset.disruptorPos}%`);
          }
          if (valEl && activeCard.dataset.disruptorPh) {
            valEl.textContent = `pH ${activeCard.dataset.disruptorPh}`;
          }
          if (idxEl && activeCard.dataset.disruptorIndex) {
            idxEl.textContent = `FACTOR ${activeCard.dataset.disruptorIndex}`;
          }
          if (impactEl && activeCard.dataset.disruptorImpact) {
            impactEl.textContent = activeCard.dataset.disruptorImpact;
          }
        }
      };

      const renderFrame = (p, scrollTab = true, forceAll = false) => {
        if (!metrics) computeMetrics();
        const { vw, gap, wClosed, wOpen, wMediaOpen, wContent, imgSize, totalStrip } = metrics;
        const clampedP = clampVal(p, 0, N - 1);
        const k = Math.min(N - 2, Math.max(0, Math.floor(clampedP)));
        const t = clampVal(clampedP - k, 0, 1);

        for (let i = 0; i < N; i++) {
          let w = 0;
          if (i === k) w = 1 - t;
          else if (i === k + 1) w = t;

          if (!forceAll && Math.abs(w - lastW[i]) < 0.00005) continue;
          lastW[i] = w;

          const cardW = wClosed + w * (wOpen - wClosed);
          // Stage 1 (cardW <= wMediaOpen): photo fills 100% of card (0px white strip).
          // Stage 2 (cardW > wMediaOpen): photo locks at wMediaOpen (zero reversal) while editorial drawer opens 0 -> wContent.
          const mediaW = Math.min(cardW, wMediaOpen);
          const drawerW = Math.max(0, cardW - mediaW);
          const u = wContent > 0 ? clampVal(drawerW / wContent, 0, 1) : 0;
          const { card, media, content, img } = cardParts[i];

          const cardWStr = `${cardW.toFixed(2)}px`;
          card.style.flexBasis = cardWStr;
          card.style.width = cardWStr;
          card.style.setProperty('--dv-open', w.toFixed(4));

          if (media) {
            media.style.width = `${mediaW.toFixed(2)}px`;
          }
          if (img) {
            const imgX = (mediaW - imgSize) * 0.5;
            const imgScale = 1.06 - 0.06 * w;
            img.style.transform = `translate3d(${imgX.toFixed(2)}px, 0, 0) scale(${imgScale.toFixed(4)})`;
          }
          if (content) {
            const textAlpha = smoothstep(clampVal((u - 0.38) / 0.58, 0, 1));
            const slideX = -(1 - textAlpha) * 14;
            content.style.opacity = textAlpha.toFixed(3);
            content.style.transform = `translate3d(${slideX.toFixed(2)}px, 0, 0)`;
          }
        }

        const maxTx = Math.max(0, totalStrip - vw);
        if (maxTx <= 2) {
          track.style.transform = 'translate3d(0px, 0, 0)';
        } else {
          const anchorFor = m => {
            if (m <= 0) return 0;
            if (m >= N - 1) return maxTx;
            const leftEdge = m * (wClosed + gap);
            const ideal = leftEdge - (vw - wOpen) * 0.5;
            return clampVal(ideal, 0, maxTx);
          };
          const tx = anchorFor(k) + t * (anchorFor(k + 1) - anchorFor(k));
          track.style.transform = `translate3d(-${tx.toFixed(2)}px, 0, 0)`;
        }

        if (scrubber) {
          const pct = N > 1 ? (clampedP / (N - 1)) * 100 : 0;
          scrubber.style.setProperty('--dv-scroll-pct', pct.toFixed(2));
        }

        syncActiveMeta(Math.round(clampedP), scrollTab);
      };

      const tick = now => {
        rafId = 0;
        const dt = lastTickTime ? clampVal((now - lastTickTime) / 1000, 0.001, 0.032) : 0.016;
        lastTickTime = now;

        const omega = isDirectInput ? 21.0 : 11.4;
        const kSpring = omega * omega;
        const cDamp = 2.0 * omega;
        const maxVel = isDirectInput ? 3.8 : 2.75;
        const steps = 3;
        const subDt = dt / steps;

        for (let s = 0; s < steps; s++) {
          const disp = currentP - targetP;
          const accel = -kSpring * disp - cDamp * velocityP;
          velocityP = clampVal(velocityP + accel * subDt, -maxVel, maxVel);
          currentP = clampVal(currentP + velocityP * subDt, 0, N - 1);
        }

        const remaining = Math.abs(targetP - currentP);
        const speed = Math.abs(velocityP);
        if (remaining <= 0.0006 && speed <= 0.006) {
          currentP = targetP;
          velocityP = 0;
          lastTickTime = 0;
          renderFrame(currentP, true);
          return;
        }

        renderFrame(currentP, !isDirectInput);
        rafId = requestAnimationFrame(tick);
      };

      const scheduleTick = () => {
        if (!rafId) {
          lastTickTime = performance.now();
          rafId = requestAnimationFrame(tick);
        }
      };

      const animateToIndex = idx => {
        isDirectInput = false;
        targetP = clampVal(idx, 0, N - 1);
        scheduleTick();
      };

      computeMetrics();
      renderFrame(0, false, true);

      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(() => {
          computeMetrics();
          renderFrame(currentP, false, true);
        });
        ro.observe(viewport);
      } else {
        window.addEventListener('resize', () => {
          computeMetrics();
          renderFrame(currentP, false, true);
        }, { passive: true });
      }

      // 1. Trackpad / Horizontal Wheel Scroll (continuous progressive flow, zero brick-wall freeze)
      let wheelSettleTimer = 0;
      let wheelDir = 0;
      let lastWheelPeak = 0;
      section.addEventListener('wheel', event => {
        const absX = Math.abs(event.deltaX);
        const absY = Math.abs(event.deltaY);
        const isHorizontal = absX > absY * 0.5 || (event.shiftKey && absY > 0);
        if (!isHorizontal) return;

        const rawDelta = absX >= absY ? event.deltaX : event.deltaY;
        if (Math.abs(rawDelta) < 0.4) return;

        const pxDelta = event.deltaMode === 1 ? rawDelta * 24 : rawDelta;
        if ((pxDelta < 0 && currentP <= 0.001 && targetP <= 0.001) ||
            (pxDelta > 0 && currentP >= N - 1.001 && targetP >= N - 1.001)) {
          return;
        }

        event.preventDefault();
        const absPx = Math.abs(pxDelta);

        // If already settling and only receiving tiny macOS trackpad inertia tail (< 3.5px), let the spring finish cleanly
        if (!isDirectInput && absPx < 3.5 && absPx < lastWheelPeak * 0.35) {
          return;
        }

        if (!isDirectInput) {
          isDirectInput = true;
          targetP = currentP;
          lastWheelPeak = absPx;
        } else if (absPx > lastWheelPeak) {
          lastWheelPeak = absPx;
        }

        wheelDir = Math.sign(pxDelta);
        const nextTarget = targetP + pxDelta / 185;
        // Keep targetP within 1.15 cards of currentP so fast spins still open windows one by one
        const minLead = Math.max(0, currentP - 1.15);
        const maxLead = Math.min(N - 1, currentP + 1.15);
        targetP = clampVal(nextTarget, minLead, maxLead);
        scheduleTick();

        window.clearTimeout(wheelSettleTimer);
        const settleDelay = absPx < 5 ? 55 : 105;
        wheelSettleTimer = window.setTimeout(() => {
          isDirectInput = false;
          lastWheelPeak = 0;
          targetP = clampVal(Math.round(currentP + wheelDir * 0.26), 0, N - 1);
          wheelDir = 0;
          scheduleTick();
        }, settleDelay);
      }, { passive: false });

      // 2. Touch Swipe on Mobile & Tablet (1:1 finger tracking + seamless velocity handoff to spring)
      let touchState = null;
      viewport.addEventListener('touchstart', event => {
        if (event.touches.length !== 1) return;
        window.clearTimeout(wheelSettleTimer);
        const touch = event.touches[0];
        touchState = {
          startX: touch.clientX,
          startY: touch.clientY,
          lastX: touch.clientX,
          lastTime: performance.now(),
          vP: 0,
          startP: currentP,
          baseIndex: Math.round(currentP),
          axis: null
        };
      }, { passive: true });

      viewport.addEventListener('touchmove', event => {
        if (!touchState || event.touches.length !== 1) return;
        const touch = event.touches[0];
        const dxTotal = touch.clientX - touchState.startX;
        const dyTotal = touch.clientY - touchState.startY;

        if (!touchState.axis) {
          if (Math.abs(dxTotal) < 5 && Math.abs(dyTotal) < 5) return;
          touchState.axis = Math.abs(dxTotal) >= Math.abs(dyTotal) * 0.75 ? 'x' : 'y';
          if (touchState.axis === 'x') {
            isDirectInput = true;
            targetP = currentP;
          }
        }

        if (touchState.axis === 'x') {
          event.preventDefault();
          const now = performance.now();
          const dt = Math.max(0.004, (now - touchState.lastTime) / 1000);
          const stepDx = touch.clientX - touchState.lastX;
          touchState.lastX = touch.clientX;
          touchState.lastTime = now;

          const vw = metrics ? metrics.vw : 360;
          const stepPx = clampVal(vw * 0.42, 130, 175);
          const dP = -stepDx / stepPx;
          const instVP = dP / dt;
          touchState.vP = touchState.vP * 0.45 + instVP * 0.55;

          isDirectInput = true;
          const minLead = Math.max(0, currentP - 1.15);
          const maxLead = Math.min(N - 1, currentP + 1.15);
          targetP = clampVal(targetP + dP, minLead, maxLead);
          scheduleTick();
        }
      }, { passive: false });

      const finishTouch = () => {
        if (!touchState) return;
        const state = touchState;
        touchState = null;
        if (state.axis !== 'x') return;
        isDirectInput = false;
        velocityP = clampVal(state.vP, -2.75, 2.75);
        const totalMovedP = currentP - state.startP;
        let nextIndex;
        if (Math.abs(state.vP) > 0.65) {
          nextIndex = Math.round(currentP + Math.sign(state.vP) * 0.38);
        } else if (Math.abs(totalMovedP) > 0.16) {
          nextIndex = Math.round(currentP + Math.sign(totalMovedP) * 0.34);
        } else {
          nextIndex = Math.round(currentP);
        }
        targetP = clampVal(nextIndex, 0, N - 1);
        scheduleTick();
      };
      viewport.addEventListener('touchend', finishTouch, { passive: true });
      viewport.addEventListener('touchcancel', finishTouch, { passive: true });

      // 3. Mouse Click & Drag on Desktop (fluid continuous drag + spring velocity handoff)
      let mouseDrag = null;
      let suppressClick = false;
      viewport.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        window.clearTimeout(wheelSettleTimer);
        mouseDrag = {
          startX: event.clientX,
          lastX: event.clientX,
          lastTime: performance.now(),
          vP: 0,
          startP: currentP,
          moved: false
        };
        suppressClick = false;
      });

      window.addEventListener('pointermove', event => {
        if (!mouseDrag) return;
        const dxTotal = event.clientX - mouseDrag.startX;
        if (!mouseDrag.moved && Math.abs(dxTotal) > 4) {
          mouseDrag.moved = true;
          suppressClick = true;
          isDirectInput = true;
          targetP = currentP;
          track.classList.add('is-dragging');
        }
        if (mouseDrag.moved) {
          const now = performance.now();
          const dt = Math.max(0.004, (now - mouseDrag.lastTime) / 1000);
          const stepDx = event.clientX - mouseDrag.lastX;
          mouseDrag.lastX = event.clientX;
          mouseDrag.lastTime = now;

          const stepPx = 185;
          const dP = -stepDx / stepPx;
          mouseDrag.vP = mouseDrag.vP * 0.5 + (dP / dt) * 0.5;

          isDirectInput = true;
          const minLead = Math.max(0, currentP - 1.15);
          const maxLead = Math.min(N - 1, currentP + 1.15);
          targetP = clampVal(targetP + dP, minLead, maxLead);
          scheduleTick();
        }
      });

      const endMouseDrag = event => {
        if (!mouseDrag) return;
        const state = mouseDrag;
        const totalDx = (event?.clientX ?? state.lastX) - state.startX;
        mouseDrag = null;
        track.classList.remove('is-dragging');
        if (state.moved) {
          isDirectInput = false;
          velocityP = clampVal(state.vP, -2.75, 2.75);
          if (Math.abs(state.vP) > 0.55) {
            targetP = clampVal(Math.round(currentP + Math.sign(state.vP) * 0.35), 0, N - 1);
          } else if (Math.abs(totalDx) > 20) {
            targetP = clampVal(Math.round(currentP + Math.sign(-totalDx) * 0.32), 0, N - 1);
          } else {
            targetP = clampVal(Math.round(currentP), 0, N - 1);
          }
          scheduleTick();
          window.setTimeout(() => { suppressClick = false; }, 80);
        }
      };
      window.addEventListener('pointerup', endMouseDrag);
      window.addEventListener('pointercancel', endMouseDrag);

      // 4. Interactive Bottom Scrubber Bar (progressive wave across windows)
      if (scrubber) {
        let isScrubbing = false;
        let lastScrubP = 0;
        let scrubDir = 0;
        const scrubAt = clientX => {
          const rect = scrubber.getBoundingClientRect();
          if (!rect.width) return;
          const ratio = clampVal((clientX - rect.left) / rect.width, 0, 1);
          isDirectInput = true;
          const nextP = ratio * (N - 1);
          if (Math.abs(nextP - lastScrubP) > 0.01) {
            scrubDir = Math.sign(nextP - lastScrubP);
            lastScrubP = nextP;
          }
          targetP = nextP;
          scheduleTick();
        };
        scrubber.addEventListener('pointerdown', event => {
          isScrubbing = true;
          lastScrubP = currentP;
          scrubDir = 0;
          scrubber.setPointerCapture?.(event.pointerId);
          scrubAt(event.clientX);
        });
        scrubber.addEventListener('pointermove', event => {
          if (!isScrubbing) return;
          scrubAt(event.clientX);
        });
        const stopScrub = () => {
          if (!isScrubbing) return;
          isScrubbing = false;
          isDirectInput = false;
          targetP = clampVal(Math.round(currentP + scrubDir * 0.28), 0, N - 1);
          scrubDir = 0;
          scheduleTick();
        };
        scrubber.addEventListener('pointerup', stopScrub);
        scrubber.addEventListener('pointercancel', stopScrub);
      }

      // 5. Card Click, Keyboard, Tabs, Dots & Arrows
      cards.forEach((card, index) => {
        card.addEventListener('click', () => {
          if (suppressClick) return;
          animateToIndex(index);
        });
        card.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            animateToIndex(index);
          } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault();
            const next = clampVal(Math.round(targetP) + 1, 0, N - 1);
            cards[next]?.focus();
            animateToIndex(next);
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault();
            const prev = clampVal(Math.round(targetP) - 1, 0, N - 1);
            cards[prev]?.focus();
            animateToIndex(prev);
          }
        });
      });

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => animateToIndex(index));
      });

      dots.forEach((dot, index) => {
        dot.addEventListener('click', () => animateToIndex(index));
      });

      prevBtn?.addEventListener('click', () => {
        animateToIndex(Math.round(targetP) - 1);
      });
      nextBtn?.addEventListener('click', () => {
        animateToIndex(Math.round(targetP) + 1);
      });
    });
  }
  window.DearVGalleryLightbox = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  window.addEventListener('pageshow', () => mount());
})();

/* ==========================================================================
   DEAR V CALLOUT BOTTLE KINETICS (3D Pectin Jelly Touch/Hover & Scroll Physics)
   - Mobile (< 750px): Tactile vertical + lateral touch-scroll glide & pectin jelly.
   - Desktop (>= 750px): Wheel-tick-proof 3D orbital perspective turn & motif counter-parallax
     on scroll, plus plush 3D pectin jelly response on direct cursor hover.
   ========================================================================== */
(() => {
  if (window.DearVCalloutBottleKinetic) return;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  let rafId = 0;
  let lastScrollY = window.scrollY || 0;
  let lastFrameTime = 0;

  const clamp = (val, min, max) => Math.min(max, Math.max(min, val));
  const dampFactor = (rate, dt) => 1 - Math.pow(1 - rate, dt);

  function isDesktopViewport() {
    return window.innerWidth >= 750;
  }

  function wakeLoop() {
    if (!rafId && !reducedMotion.matches) {
      rafId = window.requestAnimationFrame(stepLoop);
    }
  }

  function stepLoop(now) {
    rafId = 0;
    if (reducedMotion.matches) {
      lastFrameTime = 0;
      return;
    }
    const dt = lastFrameTime ? clamp((now - lastFrameTime) / 16.6667, 0.5, 2.2) : 1;
    lastFrameTime = now;

    const desktop = isDesktopViewport();
    let activeCount = 0;

    for (const [el, st] of instances) {
      // Smooth pointer targets first to eliminate mouse/touch sample jitter
      const ptrLerp = dampFactor(0.26, dt);
      st.tx += (st.ptx - st.tx) * ptrLerp;
      st.ty += (st.pty - st.ty) * ptrLerp;
      st.trx += (st.ptrx - st.trx) * ptrLerp;
      st.try += (st.ptry - st.try) * ptrLerp;

      // Smooth viewport scroll progress (eliminates stepped mouse-wheel jumps on desktop)
      const progLerp = dampFactor(desktop ? 0.085 : 0.14, dt);
      st.smoothProgress += (st.targetProgress - st.smoothProgress) * progLerp;

      let targetX = st.tx;
      let targetY = st.ty;
      let targetRx = st.trx;
      let targetRy = st.try;
      let scrollRot = 0;

      if (desktop) {
        // Desktop scroll: silky 3D orbital turn + subtle lateral poise (no up/down wheel bounce)
        const sp = st.smoothProgress;
        const scrollX = Math.sin(sp * Math.PI) * 1.8;
        const scrollY = sp * -2.0;
        targetX += scrollX;
        targetY += scrollY;
        targetRx += -sp * 1.85;
        targetRy += sp * 4.2;
        scrollRot = sp * 0.75;
        st.scrollTargetX *= Math.pow(0.85, dt);
        st.scrollTargetY *= Math.pow(0.85, dt);
      } else {
        // Mobile scroll: tactile vertical + lateral plush drift
        st.scrollTargetY *= Math.pow(0.89, dt);
        st.scrollTargetX *= Math.pow(0.90, dt);
        targetX += st.scrollTargetX;
        targetY += st.scrollTargetY;
        targetRx += clamp(-st.scrollTargetY * 0.14, -1.6, 1.6);
        targetRy += clamp(st.scrollTargetX * 0.45, -1.8, 1.8);
      }

      const stiffness = (desktop ? 0.115 : 0.13) * dt;
      const damping = Math.pow(desktop ? 0.78 : 0.80, dt);

      st.vx = (st.vx + (targetX - st.x) * stiffness) * damping;
      st.vy = (st.vy + (targetY - st.y) * stiffness) * damping;
      st.x += st.vx * dt;
      st.y += st.vy * dt;

      const rotLerp = dampFactor(0.15, dt);
      st.rx += (targetRx - st.rx) * rotLerp;
      st.ry += (targetRy - st.ry) * rotLerp;
      st.stageY += (st.targetStageY - st.stageY) * dampFactor(0.11, dt);
      st.stageX += (st.targetStageX - st.stageX) * dampFactor(0.11, dt);
      st.dialDeg += (st.targetDialDeg - st.dialDeg) * dampFactor(0.095, dt);

      if (st.jellyEnergy > 0.002) {
        st.jellyPhase += 0.27 * dt;
        st.jellyEnergy *= Math.pow(0.918, dt);
      } else {
        st.jellyEnergy = 0;
      }

      const wave = Math.sin(st.jellyPhase) * st.jellyEnergy;
      const velStretch = desktop ? 0 : clamp(st.vy * 0.0032, -0.016, 0.016);
      const sx = 1 + wave * 0.024 - Math.abs(velStretch) * 0.7;
      const sy = 1 - wave * 0.024 + Math.abs(velStretch);
      const rot =
        scrollRot +
        Math.cos(st.jellyPhase * 0.85) * st.jellyEnergy * 1.05 +
        clamp(st.vx * 0.14 + (desktop ? 0 : st.vy * 0.06), -1.2, 1.2);

      el.style.setProperty('--dv-kin-x', `${st.x.toFixed(2)}px`);
      el.style.setProperty('--dv-kin-y', `${st.y.toFixed(2)}px`);
      el.style.setProperty('--dv-kin-rx', `${st.rx.toFixed(2)}deg`);
      el.style.setProperty('--dv-kin-ry', `${st.ry.toFixed(2)}deg`);
      el.style.setProperty('--dv-kin-rot', `${rot.toFixed(2)}deg`);
      el.style.setProperty('--dv-kin-sx', sx.toFixed(4));
      el.style.setProperty('--dv-kin-sy', sy.toFixed(4));
      el.style.setProperty('--dv-stage-y', `${st.stageY.toFixed(2)}px`);
      el.style.setProperty('--dv-stage-x', `${(st.stageX - st.x * 0.38).toFixed(2)}px`);
      el.style.setProperty('--dv-stage-scale', (1 + st.jellyEnergy * 0.02).toFixed(3));
      el.style.setProperty('--dv-dial-scroll', `${st.dialDeg.toFixed(2)}deg`);

      const unsettled =
        Math.abs(st.vx) > 0.012 ||
        Math.abs(st.vy) > 0.012 ||
        Math.abs(targetX - st.x) > 0.025 ||
        Math.abs(targetY - st.y) > 0.025 ||
        Math.abs(targetRx - st.rx) > 0.025 ||
        Math.abs(targetRy - st.ry) > 0.025 ||
        Math.abs(st.targetProgress - st.smoothProgress) > 0.003 ||
        Math.abs(st.targetDialDeg - st.dialDeg) > 0.04 ||
        st.jellyEnergy > 0.002;

      if (unsettled) activeCount += 1;
    }

    if (activeCount > 0) {
      rafId = window.requestAnimationFrame(stepLoop);
    } else {
      lastFrameTime = 0;
    }
  }

  function updateFromPointer(el, st, clientX, clientY, isInitialImpulse = false) {
    if (reducedMotion.matches) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const nx = clamp(((clientX - rect.left) / rect.width - 0.5) * 2, -1, 1);
    const ny = clamp(((clientY - rect.top) / rect.height - 0.5) * 2, -1, 1);

    st.hovering = true;
    el.classList.add('is-kinetic-active');

    st.ptx = nx * 5.2;
    st.pty = ny * 4.2 - 1.8;
    st.ptrx = -ny * 2.4;
    st.ptry = nx * 3.0;
    if (isInitialImpulse) {
      st.jellyEnergy = Math.max(st.jellyEnergy, 0.82);
      st.jellyPhase = 0;
    } else {
      st.jellyEnergy = Math.min(0.88, Math.max(st.jellyEnergy, 0.36));
    }
    wakeLoop();
  }

  function releasePointer(el, st) {
    st.hovering = false;
    st.ptx = 0;
    st.pty = 0;
    st.ptrx = 0;
    st.ptry = 0;
    st.jellyEnergy = Math.max(st.jellyEnergy, 0.46);
    window.setTimeout(() => {
      if (!st.hovering) el.classList.remove('is-kinetic-active');
    }, 340);
    wakeLoop();
  }

  function onScroll() {
    if (reducedMotion.matches || !instances.size) return;
    const currentY = window.scrollY || 0;
    const rawDelta = currentY - lastScrollY;
    const delta = clamp(rawDelta, -55, 55);
    lastScrollY = currentY;
    if (Math.abs(delta) < 0.4) return;

    const vh = window.innerHeight || 844;
    const desktop = isDesktopViewport();

    for (const [el, st] of instances) {
      const rect = el.getBoundingClientRect();
      if (rect.bottom < -140 || rect.top > vh + 140) continue;

      const progress = clamp((vh * 0.5 - (rect.top + rect.height * 0.5)) / (vh * 0.72), -1, 1);
      st.targetProgress = progress;

      if (desktop) {
        // Desktop: zero vertical wheel-impulse bounce; smooth 3D orbital progress + motif counter-glide
        st.targetStageY = -progress * 10.5;
        st.targetStageX = Math.sin(progress * Math.PI) * 3.2;
        st.targetDialDeg = progress * 26;
      } else {
        // Mobile: smooth velocity-filtered vertical + lateral drift & subtle pectin wave
        st.smoothScrollVel = st.smoothScrollVel * 0.65 + delta * 0.35;
        st.scrollTargetY = clamp(st.scrollTargetY - st.smoothScrollVel * 0.16, -9.5, 9.5);
        st.scrollTargetX = Math.sin(currentY * 0.0085) * 2.4;
        st.vy += clamp(-st.smoothScrollVel * 0.042, -1.6, 1.6);
        st.jellyEnergy = Math.min(0.68, Math.max(st.jellyEnergy, Math.abs(st.smoothScrollVel) * 0.011));
        st.targetStageY = progress * 11;
        st.targetStageX = 0;
        st.targetDialDeg = progress * 22;
      }
      wakeLoop();
    }
  }

  function mount(scope = document) {
    const nodes = scope.querySelectorAll?.('[data-dv-kinetic-bottle="gummies"]') || [];
    nodes.forEach(el => {
      if (instances.has(el)) return;
      const st = {
        x: 0,
        y: 0,
        vx: 0,
        vy: 0,
        tx: 0,
        ty: 0,
        ptx: 0,
        pty: 0,
        rx: 0,
        ry: 0,
        trx: 0,
        try: 0,
        ptrx: 0,
        ptry: 0,
        scrollTargetX: 0,
        scrollTargetY: 0,
        smoothScrollVel: 0,
        targetProgress: 0,
        smoothProgress: 0,
        stageY: 0,
        targetStageY: 0,
        stageX: 0,
        targetStageX: 0,
        dialDeg: 0,
        targetDialDeg: 0,
        jellyEnergy: 0,
        jellyPhase: 0,
        hovering: false
      };
      instances.set(el, st);

      el.addEventListener('pointerenter', e => updateFromPointer(el, st, e.clientX, e.clientY, true), { passive: true });
      el.addEventListener('pointermove', e => updateFromPointer(el, st, e.clientX, e.clientY, false), { passive: true });
      el.addEventListener('pointerdown', e => updateFromPointer(el, st, e.clientX, e.clientY, true), { passive: true });
      el.addEventListener('pointerleave', () => releasePointer(el, st), { passive: true });
      el.addEventListener('pointercancel', () => releasePointer(el, st), { passive: true });

      el.addEventListener('touchstart', e => {
        const t = e.touches?.[0];
        if (t) updateFromPointer(el, st, t.clientX, t.clientY, true);
      }, { passive: true });
      el.addEventListener('touchmove', e => {
        const t = e.touches?.[0];
        if (t) updateFromPointer(el, st, t.clientX, t.clientY, false);
      }, { passive: true });
      el.addEventListener('touchend', () => releasePointer(el, st), { passive: true });
    });
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.DearVCalloutBottleKinetic = { mount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  window.addEventListener('pageshow', () => mount());
})();

